import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { toast } from "sonner";
import { UserX, Ban, Clock, Mic, Search } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function MembersPanel() {
  const { active } = useServer();
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");

  const guildId = active?.id;

  const refresh = () => {
    if (!guildId || !active.bot_present) return;
    api.get(`/bot/members?guild_id=${guildId}`).then((r) => setMembers(r.data));
  };

  useEffect(() => { refresh(); }, [guildId, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;

  const act = async (member_id, action, username) => {
    if ((action === "kick" || action === "ban") && !window.confirm(`${action.toUpperCase()} ${username}?`)) return;
    try {
      await api.post(`/bot/members/action?guild_id=${guildId}`, {
        member_id, action, reason: action === "ban" ? "Violation of community rules" : "Administrative action",
      });
      toast.success(`${action} executed on ${username}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const filtered = members.filter(
    (m) =>
      m.username.toLowerCase().includes(search.toLowerCase()) ||
      (m.nickname || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="members-panel" className="space-y-8">
      <div>
        <div className="overline mb-2">// PERSONNEL</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Members</h1>
        <p className="text-zinc-400 mt-2">Managing <span className="text-white">{active.name}</span> · {members.length} members</p>
      </div>

      <div className="tactical-card p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input className="tactical-input pl-10" placeholder="Search members…" value={search}
                   onChange={(e) => setSearch(e.target.value)} data-testid="members-search-input" />
          </div>
          <span className="font-mono-tabular text-sm text-zinc-500">{filtered.length} / {members.length}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((m) => (
            <div key={m.id} className="border border-brand-line p-4 hover:border-brand-orange transition-colors rounded-lg" data-testid={`member-card-${m.id}`}>
              <div className="flex items-center gap-3">
                <div className="relative">
                  {m.avatar ? (
                    <img src={m.avatar} alt={m.username} className="w-11 h-11 rounded-lg object-cover" />
                  ) : (
                    <div className="w-11 h-11 rounded-lg bg-brand-orange/10 border border-brand-orange flex items-center justify-center font-bold text-brand-orange uppercase">
                      {m.username[0]}
                    </div>
                  )}
                  <span className={`status-dot status-${m.status} absolute -bottom-0.5 -right-0.5 ring-2 ring-black`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{m.username}</div>
                  <div className="text-xs text-zinc-500 truncate">{m.nickname || "No nickname"}</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {m.is_owner && <span className="font-mono-tabular text-[10px] uppercase tracking-wider border border-yellow-500 text-yellow-400 px-2 py-0.5 rounded">OWNER</span>}
                {m.roles.slice(0, 4).map((r) => (
                  <span key={r} className="font-mono-tabular text-[10px] uppercase tracking-wider border border-brand-orange/40 text-brand-orange px-2 py-0.5 rounded">{r}</span>
                ))}
                {m.is_muted && <span className="font-mono-tabular text-[10px] uppercase tracking-wider border border-yellow-500/40 text-yellow-400 px-2 py-0.5 rounded">TIMED OUT</span>}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {m.is_muted ? (
                  <button onClick={() => act(m.id, "unmute", m.username)} className="btn-ghost border border-brand-line text-xs px-3 py-1.5" data-testid={`member-unmute-${m.id}`}>
                    <Mic className="w-3.5 h-3.5" /> Unmute
                  </button>
                ) : (
                  <button onClick={() => act(m.id, "timeout", m.username)} className="btn-ghost border border-brand-line text-xs px-3 py-1.5" data-testid={`member-timeout-${m.id}`}>
                    <Clock className="w-3.5 h-3.5" /> Timeout
                  </button>
                )}
                {!m.is_owner && (
                  <>
                    <button onClick={() => act(m.id, "kick", m.username)} className="btn-ghost border border-brand-line text-xs px-3 py-1.5 hover:!text-yellow-400" data-testid={`member-kick-${m.id}`}>
                      <UserX className="w-3.5 h-3.5" /> Kick
                    </button>
                    <button onClick={() => act(m.id, "ban", m.username)} className="btn-ghost border border-brand-line text-xs px-3 py-1.5 hover:!text-red-400" data-testid={`member-ban-${m.id}`}>
                      <Ban className="w-3.5 h-3.5" /> Ban
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-zinc-500 font-mono-tabular text-sm">NO MEMBERS FOUND</div>
        )}
      </div>
    </div>
  );
}
